/**
 * 草稿箱批量发布：每条草稿走与发品页相同的规则/属性/SKU 校验，再提交 publishOrEdit。
 * 平台预检约 40/s；发品提交安全上限 35/s（同店）。草稿箱批量仍串行提交以对齐同店 FIFO。
 */

import { fixedUi } from '@/i18n/fixedUi';
import { temuPlatformAPI } from '@/utils/api';
import { parseAttributeTemplate, resolveSkcColorAgainstTemplate } from '@/temu-platform/utils/temuAttributeTemplate';
import { buildTemuPublishPayload, samplePublishPayloadCurrency } from '@/temu-platform/utils/buildTemuPublishPayload';
import {
  resolveSkuPackageCapabilities,
} from '@/temu-platform/utils/temuPublishFillInStandard';
import { resolveShopPublishCurrencies } from '@/temu-platform/utils/resolveTemuPublishCurrency';
import { runTemuPublishPreflight } from '@/temu-platform/utils/temuPublishPreflight';
import { extractTemuPublishFailMessage, extractTemuPublishedSpu, isTemuPublishSuccess } from '@/temu-platform/utils/temuPublishResult';
import { pruneSizeChartForSubmit, skuSizeKey } from '@/temu-platform/utils/temuSizeChart';
import { validateTemuPublishForm } from '@/temu-platform/utils/validateTemuPublishForm';
import { alignAttributesToCategoryTemplate } from '@/temu-platform/utils/temuProductAttributeTemplate';
import { ensureTemuFormTitlesLocalized } from '@/pages/ipe/workbench/utils/applyListingItemToTemuPublishForm';
import {
  applyShopBrandsToAttrTemplate,
  normalizeBrandAttributeSelections,
} from '@/temu-platform/utils/temuShopBrandAttrs';
import { isTemuModelRequiredForCategory } from '@/temu-platform/utils/temuModelRequiredCategories';
import { isTemuNetWeightRequiredForCategory } from '@/temu-platform/utils/temuNetWeightRequiredCategories';
import { isTemuSemiSkuNetContentRequired } from '@/temu-platform/utils/temuSemiNetContentRequiredCategories';
import { fetchSizeChartClassRequired } from '@/temu-platform/modules/products/components/TemuOfficialSizeChartPanel';
import {
  officialSizeChartDraftHasContent,
  resolveOfficialSizeTemplateIds,
} from '@/temu-platform/utils/temuOfficialSizeChart';
import { isTemuShoeSizeClass } from '@/temu-platform/utils/temuShoeSizeFootMap';
import { ensureTemuColorBlocksOnForm } from '@/temu-platform/utils/temuColorBlock';
import { ensureTemuMaterialFromMainOnForm } from '@/temu-platform/utils/temuMaterialFromMain';
import { ensureTemuFormImagesHosted } from '@/pages/ipe/workbench/utils/convertListingImageToTemu';
import { ensureTemuCustomColorSpecs } from '@/temu-platform/utils/ensureTemuCustomColorSpecs';
import {
  enrichTemuFormPrintNames,
  filterSkcsReadyForPlatformPublish,
  LISTING_NO_PUBLISHABLE_COLOR_MESSAGE,
  printNamesFromTemuPrintImages,
} from '@/pages/ipe/workbench/utils/applyListingItemToTemuPublishForm';
import {
  EMPTY_TEMU_PRINT_INFRINGEMENT,
  normalizeTemuPrintInfringement,
} from '@/temu-platform/utils/temuPublishPrintInfringement';
import { normalizeTemuOriginRegion } from '@/temu-platform/utils/temuProductOrigin';

/** 预检等读接口并发上限（平台约 40/s） */
export const TEMU_BATCH_PUBLISH_CONCURRENCY = 40;

/** 兼容 getShopWarehouses 多种返回形态 */
export function normalizeTemuWarehouseList(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.warehouses)) return raw.warehouses;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.list)) return raw.list;
  return [];
}

/** 未选仓库时默认第一个商家仓（与发品页 withDefaultWarehouse 对齐） */
export function withDefaultTemuWarehouse(form, warehouses = []) {
  const next = form && typeof form === 'object' ? { ...form } : {};
  const rows = Array.isArray(warehouses) ? warehouses : [];
  if (!rows.length) return next;
  const codes = rows
    .map((item) => String(item?.id || item?.warehouseCode || '').trim())
    .filter(Boolean);
  const current = String(next.warehouseId || '').trim();
  if (current && codes.includes(current)) {
    if (!String(next.warehouseName || '').trim()) {
      const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
      next.warehouseName = hit?.name || hit?.warehouseName || '';
    }
    return next;
  }
  const first = rows[0];
  const id = String(first?.id || first?.warehouseCode || '').trim();
  if (!id) return next;
  next.warehouseId = id;
  next.warehouseName = first?.name || first?.warehouseName || '';
  return next;
}

async function resolveNeedGuideFileFlag(shopId, form, shopType = '', cache = null) {
  const catId = Number(form?.categoryId || 0);
  if (!shopId || !(catId > 0)) return false;
  const isSemi = /半托/i.test(String(form?.shopType || shopType || ''));
  const bindSiteIds = isSemi
    ? (form?.selectedSites || []).map((id) => Number(id)).filter((id) => id > 0)
    : [];
  const cacheKey = `${shopId}:${catId}:${isSemi ? bindSiteIds.join(',') : 'all'}`;
  if (cache?.has(cacheKey)) {
    try {
      return await cache.get(cacheKey);
    } catch {
      return false;
    }
  }
  const pending = (async () => {
    try {
      const res = await temuPlatformAPI.getPublishCatsMandatory(shopId, {
        leafCatId: catId,
        configItems: [1],
        bindSiteIds: isSemi ? bindSiteIds : undefined,
      });
      return Boolean(res?.needGuideFile ?? res?.result?.needGuideFile ?? false);
    } catch {
      return false;
    }
  })();
  if (cache) cache.set(cacheKey, pending);
  return pending;
}

async function resolveSizeChartClassCached(shopId, categoryId, shopType = '', cache = null) {
  const catId = Number(categoryId || 0);
  if (!shopId || !(catId > 0)) return null;
  const cacheKey = `${shopId}:${catId}:${String(shopType || '').trim()}`;
  if (cache?.has(cacheKey)) {
    try {
      return await cache.get(cacheKey);
    } catch {
      return null;
    }
  }
  const pending = fetchSizeChartClassRequired(shopId, catId, { shopType })
    .catch(() => null);
  if (cache) {
    cache.set(cacheKey, pending);
    pending.then((res) => {
      if (!res) cache.delete(cacheKey);
    });
  }
  return pending;
}

function emptyPrepareCaches() {
  return {
    fillInStandard: new Map(),
    sizeChartClass: new Map(),
    needGuideFile: new Map(),
    sizeTemplateIds: new Map(),
  };
}

export { emptyPrepareCaches };

/** 同模板批量：尺码表只创建一次，后续条目复用 sizeTemplateIds */
function sizeTemplateCacheKey(shopId, form) {
  const catId = Number(form?.categoryId || 0);
  const templateId = String(form?.selectedProductTemplateId || '').trim();
  const draft = form?.officialSizeChart && typeof form.officialSizeChart === 'object'
    ? form.officialSizeChart
    : null;
  const mode = String(draft?.mode || '').trim();
  const selected = Array.isArray(draft?.selectedBusinessIds)
    ? draft.selectedBusinessIds.map(Number).filter((id) => id > 0).join(',')
    : '';
  const recordCount = Array.isArray(draft?.records) ? draft.records.length : 0;
  return `${shopId}:${catId}:${templateId || 'none'}:${mode}:${selected}:${recordCount}`;
}

/** 同店发品提交安全上限：平台约 40/s，预留余量用 35 */
export const TEMU_PUBLISH_SUBMIT_MAX_PER_SECOND = 35;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * 滑动窗口限流：同一店铺每秒最多 acquire maxPerSecond 次。
 * @param {number} maxPerSecond
 */
export function createPerSecondRateLimiter(maxPerSecond = TEMU_PUBLISH_SUBMIT_MAX_PER_SECOND) {
  const limit = Math.max(1, Number(maxPerSecond) || TEMU_PUBLISH_SUBMIT_MAX_PER_SECOND);
  const stamps = [];
  return {
    async acquire() {
      while (true) {
        const now = Date.now();
        while (stamps.length && now - stamps[0] >= 1000) stamps.shift();
        if (stamps.length < limit) {
          stamps.push(now);
          return;
        }
        const waitMs = Math.max(1, 1000 - (now - stamps[0]));
        // eslint-disable-next-line no-await-in-loop
        await sleep(waitMs);
      }
    },
  };
}

function deriveFormCurrency(initial = {}) {
  const top = String(initial.currency || '').trim();
  if (top) return top;
  for (const skc of initial.skcList || []) {
    for (const sku of skc.skuRows || []) {
      const code = String(sku?.currency || '').trim();
      if (code) return code;
    }
  }
  return '';
}

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  (skcList || []).forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

function normalizeProductAttributes(raw = {}) {
  const out = {};
  Object.entries(raw || {}).forEach(([id, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      const asComposition = value.some((row) => (
        row && typeof row === 'object' && ('percent' in row || 'attributeExtraValue' in row || 'valueId' in row)
      ));
      if (asComposition) {
        out[id] = value
          .map((row) => {
            if (row != null && (typeof row === 'string' || typeof row === 'number')) {
              return { valueId: String(row), percent: '' };
            }
            if (!row || typeof row !== 'object') return null;
            const valueId = row.valueId ?? row.attributeValueId;
            if (valueId == null || valueId === '') return null;
            return {
              valueId: String(valueId),
              percent: String(row.percent ?? row.attributeExtraValue ?? ''),
            };
          })
          .filter(Boolean);
        return;
      }
      out[id] = value
        .map((row) => {
          if (row && typeof row === 'object') return String(row.valueId ?? row.attributeValueId ?? '');
          return String(row ?? '');
        })
        .filter(Boolean);
      return;
    }
    if (typeof value === 'object') {
      const valueId = value.valueId ?? value.attributeValueId;
      const percent = value.percent ?? value.attributeExtraValue;
      if (valueId != null && valueId !== '') {
        if (percent != null && String(percent).trim() !== '') {
          out[id] = [{ valueId: String(valueId), percent: String(percent) }];
        } else {
          out[id] = String(valueId);
        }
      } else if (value.attributeExtraValue != null && String(value.attributeExtraValue).trim() !== '') {
        out[id] = String(value.attributeExtraValue);
      }
      return;
    }
    out[id] = value;
  });
  return out;
}

/** 纯数字/无字母短串：多半是模板 ID 误写入 title/productName，不能当商品名。 */
function isJunkTemuTitleCandidate(value) {
  const text = String(value || '').trim();
  if (!text) return true;
  if (/[\u3400-\u9fff]/.test(text)) return false;
  if (/[A-Za-zÀ-ÿ]/.test(text)) return false;
  return true;
}

/** 组包前规范化表单；必须保留站内印花图，供编辑页回填。 */
export function emptyPublishForm(initial = {}) {
  const titleRaw = String(initial.title || '').trim();
  const productNameRaw = String(initial.productName || '').trim();
  // 勿用「纯数字 productName」顶替空 title（常见：模板 ID / 规则 ID 串进 productName）
  const rawTitle = (!isJunkTemuTitleCandidate(titleRaw) ? titleRaw : '')
    || (!isJunkTemuTitleCandidate(productNameRaw) ? productNameRaw : '')
    || '';
  const rawTitleEn = String(initial.titleEn || initial.englishTitle || '').trim();
  const titleLooksEnglishOnly = rawTitle && !/[\u3400-\u9fff]/.test(rawTitle);
  const originRegion = normalizeTemuOriginRegion(
    initial.originRegion
    || initial.originCountry
    || initial.productOrigin?.region1ShortName
    || initial.productOrigin?.countryShortName,
  );
  const originRegion2Raw = initial.originRegion2Id
    ?? initial.region2Id
    ?? initial.productOrigin?.region2Id;
  const originRegion2Id = originRegion2Raw != null && String(originRegion2Raw).trim() !== ''
    ? String(originRegion2Raw)
    : '';
  return {
    // 保留模板/草稿里的扩展字段（外包装、运费模板、素材图等），避免组包时被裁掉
    ...initial,
    spuName: initial.spuName || '',
    title: rawTitle,
    titleEn: rawTitleEn || (titleLooksEnglishOnly ? rawTitle : ''),
    description: initial.description || '',
    categoryId: initial.categoryId || '',
    productTypeId: initial.productTypeId || '',
    categoryPathLabel: initial.categoryPathLabel || '',
    supplierCode: initial.supplierCode || '',
    brandCode: initial.brandCode || '',
    originCountry: originRegion || initial.originCountry || '',
    originRegion: originRegion || 'CN',
    originRegion2Id,
    originCertFiles: Array.isArray(initial.originCertFiles) ? initial.originCertFiles : [],
    ipId: initial.ipId || '',
    ipName: initial.ipName || '',
    ipNameCn: initial.ipNameCn || '',
    productAttributes: normalizeProductAttributes(initial.productAttributes || {}),
    skcList: Array.isArray(initial.skcList) ? initial.skcList : [],
    selectedSizeIds: Array.isArray(initial.selectedSizeIds)
      ? initial.selectedSizeIds
      : deriveSelectedSizeIds(initial.skcList),
    sizeChart: initial.sizeChart || {},
    sizeSaleAttributeId: initial.sizeSaleAttributeId || '',
    sizeValueLabels: initial.sizeValueLabels && typeof initial.sizeValueLabels === 'object'
      ? initial.sizeValueLabels
      : {},
    // 站内印花图：任务刊登必须带入编辑快照，不能在组包时丢掉
    printImages: Array.isArray(initial.printImages) ? initial.printImages : [],
    ipePrintImageNames: Array.isArray(initial.ipePrintImageNames)
      ? initial.ipePrintImageNames
      : printNamesFromTemuPrintImages(initial.printImages),
    ipePrintInfringement: normalizeTemuPrintInfringement(
      initial.ipePrintInfringement || EMPTY_TEMU_PRINT_INFRINGEMENT,
    ),
    supplyPrice: initial.supplyPrice || '',
    currency: deriveFormCurrency(initial),
    stockQty: initial.stockQty || '',
    weightGrams: initial.weightGrams || '',
    weightUnit: initial.weightUnit || 'g',
    pkgLength: initial.pkgLength || '10',
    pkgWidth: initial.pkgWidth || '10',
    pkgHeight: initial.pkgHeight || '10',
    dimUnit: initial.dimUnit || 'cm',
    warehouseId: initial.warehouseId || '',
    warehouseName: initial.warehouseName || '',
    selectedSites: Array.isArray(initial.selectedSites) ? initial.selectedSites : [],
    mainSite: initial.mainSite || 'temu',
    shelfWay: initial.shelfWay || '1',
    hopeOnSaleDate: initial.hopeOnSaleDate || '',
  };
}

/**
 * @template T, R
 * @param {T[]} items
 * @param {number} concurrency
 * @param {(item: T, index: number) => Promise<R>} worker
 * @param {(done: number, total: number, result: R, index: number) => void} [onItemDone]
 */
export async function mapWithConcurrency(items, concurrency, worker, onItemDone) {
  const list = Array.isArray(items) ? items : [];
  const results = new Array(list.length);
  let cursor = 0;
  let finished = 0;
  const limit = Math.max(1, Math.min(Number(concurrency) || 1, list.length || 1));

  async function runWorker() {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= list.length) return;
      // eslint-disable-next-line no-await-in-loop
      const result = await worker(list[index], index);
      results[index] = result;
      finished += 1;
      onItemDone?.(finished, list.length, result, index);
    }
  }

  if (!list.length) return results;
  await Promise.all(Array.from({ length: limit }, () => runWorker()));
  return results;
}

async function resolveAttrTemplate(shopId, productTypeId, cache, categoryId = '') {
  const leafCatId = Number(categoryId || productTypeId);
  const key = String(leafCatId || productTypeId);
  if (cache.has(key)) return cache.get(key);
  // 与发品页一致：类目 attrs + 店铺品牌注入（品牌提交用 vid，不是 brandId）
  const pending = Promise.all([
    temuPlatformAPI.getAttributeTemplate(shopId, {
      catId: leafCatId,
      categoryId: leafCatId,
      category_id: leafCatId,
      product_type_id_list: [Number(productTypeId || leafCatId)],
    }),
    temuPlatformAPI.getShopBrands(shopId).catch(() => null),
  ]).then(([raw, brandRes]) => {
    const parsed = parseAttributeTemplate(raw, { productTypeId: String(productTypeId || leafCatId) });
    return applyShopBrandsToAttrTemplate(parsed, brandRes?.brands ?? brandRes);
  });
  cache.set(key, pending);
  try {
    return await pending;
  } catch (ex) {
    cache.delete(key);
    throw ex;
  }
}

function buildSubmitForm(form, { colorAttr, sizeAttr, alignedCurrency }) {
  const sizeValueLabels = { ...(form.sizeValueLabels || {}) };
  (sizeAttr?.values || []).forEach((v) => {
    if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
  });
  const resolvedSizeSaleAttrId = form.sizeSaleAttributeId || sizeAttr?.attributeId || '';
  const fromSkcSizes = deriveSelectedSizeIds(form.skcList).map((id) => String(id));
  const resolvedSelectedSizeIds = fromSkcSizes.length
    ? fromSkcSizes
    : (form.selectedSizeIds || []).map((id) => String(id));

  const submitFormBase = {
    ...form,
    sizeValueLabels,
    sizeSaleAttributeId: resolvedSizeSaleAttrId,
    selectedSizeIds: resolvedSelectedSizeIds,
    spuName: '',
    currency: alignedCurrency || form.currency,
    skcList: (form.skcList || []).map((skc) => {
      const resolved = resolveSkcColorAgainstTemplate({
        ...skc,
        colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
      }, colorAttr);
      return {
      ...resolved,
      colorLabel: resolved.colorIsCustom || resolved.colorExtraValue
        ? (resolved.colorExtraValue || resolved.colorLabel)
        : (colorAttr?.values?.find((v) => String(v.id) === String(resolved.colorValueId))?.name
          || resolved.colorLabel),
      skcName: '',
      skuRows: (skc.skuRows || []).map((sku) => ({
        ...sku,
        sizeAttributeId: sku.sizeAttributeId || sizeAttr?.attributeId || resolvedSizeSaleAttrId || '',
        sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
          || sku.sizeLabel
          || sizeValueLabels[String(sku.sizeValueId || '')]
          || '',
        currency: alignedCurrency || sku.currency || form.currency,
        skuCode: '',
      })),
    };
    }),
  };
  return submitFormBase;
}

export async function waitGoodsSubmitJob(shopId, jobId, onHint) {
  let latest = await temuPlatformAPI.getGoodsSubmitJob(shopId, jobId);
  while (latest && !latest.done) {
    onHint?.(latest.message || fixedUi('排队等待调用平台…'));
    // eslint-disable-next-line no-await-in-loop
    await sleep(700);
    // eslint-disable-next-line no-await-in-loop
    latest = await temuPlatformAPI.getGoodsSubmitJob(shopId, jobId);
  }
  return latest;
}

/**
 * 发品表单：属性模板 → 三步预检 → 本地校验 → 组包（与发品页一致）。
 * @returns {Promise<object>}
 */
function dedupeValidationMessages(messages = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(messages) ? messages : []).forEach((raw) => {
    const message = String(raw || '').trim();
    if (!message || seen.has(message)) return;
    seen.add(message);
    out.push(message);
  });
  return out;
}

function buildPrepareFailure({
  draftId,
  title,
  stage = 'validate',
  message = '',
  messages = [],
  meta = {},
  field,
  section,
}) {
  const deduped = dedupeValidationMessages([...messages, message]);
  return {
    ok: false,
    draftId,
    title,
    stage,
    message: deduped[0] || fixedUi('准备刊登失败'),
    messages: deduped,
    ...(field ? { field } : {}),
    ...(section ? { section } : {}),
    ...meta,
  };
}

export async function prepareFormPublish({
  shopId,
  form: rawForm,
  titleHint = '',
  draftId = null,
  meta = {},
  warehouses = [],
  attrTplCache,
  shopType = '',
  siteList = null,
  omitIncompleteSkcs = false,
  collectAllErrors = false,
  prepareCaches = null,
}) {
  const caches = prepareCaches || emptyPrepareCaches();
  const validationMessages = [];
  const pushValidationMessage = (message) => {
    const text = String(message || '').trim();
    if (!text) return;
    validationMessages.push(text);
  };
  const failPrepare = (stage, message, extra = {}) => {
    if (collectAllErrors) {
      pushValidationMessage(message);
      return null;
    }
    return buildPrepareFailure({
      draftId,
      title: extra.title,
      stage,
      message,
      messages: validationMessages,
      meta,
      field: extra.field,
      section: extra.section,
    });
  };

  let form = emptyPublishForm(rawForm || {});
  if (omitIncompleteSkcs) {
    const skcList = filterSkcsReadyForPlatformPublish(form.skcList);
    form = {
      ...form,
      skcList,
      selectedSizeIds: deriveSelectedSizeIds(skcList),
    };
    if (!skcList.length) {
      const titleEarly = String(form.title || titleHint || meta.title || '').trim() || fixedUi('未命名商品');
      const earlyFail = failPrepare('validate', LISTING_NO_PUBLISHABLE_COLOR_MESSAGE, { title: titleEarly });
      if (earlyFail) return earlyFail;
    }
  }
  // 印花图仅站内展示 / 侵权检测；货号在上传印花图时已拼图案编号
  form = await enrichTemuFormPrintNames(form);
  try {
    form = await ensureTemuFormImagesHosted(form, { shopId });
  } catch (imageErr) {
    const titleFail = String(form.title || titleHint || meta.title || '').trim() || fixedUi('未命名商品');
    const imageMsg = String(imageErr?.message || '模板图片转 Temu 图址失败').trim();
    // 工作区门禁 / 转图失败：不再继续收集其它校验，避免日志里再叠一层无关错误
    if (collectAllErrors && /工作区未验证|工作区.*过期|TEMU_WORKSPACE|未能转成 Temu|下载失败|无法签发读图/i.test(imageMsg)) {
      return buildPrepareFailure({
        draftId,
        title: titleFail,
        stage: 'validate',
        message: imageMsg,
        messages: [imageMsg],
        meta,
        field: 'skcList',
        section: 'spec',
      });
    }
    const earlyFail = failPrepare('validate', imageMsg, {
      title: titleFail,
      field: 'skcList',
      section: 'spec',
    });
    if (earlyFail) return earlyFail;
  }
  // 自动规则 / 批量准备：色块图与素材图并行（互不阻塞主图已转平台地址）
  const colorPromise = ensureTemuColorBlocksOnForm(form, { shopId }).catch((colorErr) => {
    throw Object.assign(colorErr || new Error('色块图生成失败'), { __stage: 'color' });
  });
  const materialPromise = ensureTemuMaterialFromMainOnForm(form, { shopId }).catch((materialErr) => {
    throw Object.assign(
      materialErr || new Error('素材图生成失败，请确保主图已上传或手动上传 1:1 素材图'),
      { __stage: 'material' },
    );
  });
  try {
    const [colorForm, materialForm] = await Promise.all([colorPromise, materialPromise]);
    form = {
      ...materialForm,
      skcList: Array.isArray(colorForm?.skcList) ? colorForm.skcList : materialForm.skcList,
    };
  } catch (prepErr) {
    const titleFail = String(form.title || titleHint || meta.title || '').trim() || fixedUi('未命名商品');
    if (prepErr?.__stage === 'material') {
      const earlyFail = failPrepare('validate', prepErr?.message || '素材图生成失败', {
        title: titleFail,
        field: 'materialImgUrl',
        section: 'spec',
      });
      if (earlyFail) return earlyFail;
    }
    const earlyFail = failPrepare('validate', prepErr?.message || '色块图生成失败', { title: titleFail });
    if (earlyFail) return earlyFail;
  }
  if (shopType) form = { ...form, shopType };
  // 商品库中文标题优先：表单若是空/纯数字 ID，用 titleHint 覆盖，避免把规则 ID「9」当地商品名
  const formTitle = String(form.title || form.productName || '').trim();
  const hintTitle = String(titleHint || meta.title || '').trim();
  const formTitleOk = formTitle && !isJunkTemuTitleCandidate(formTitle);
  const hintTitleOk = hintTitle && !isJunkTemuTitleCandidate(hintTitle);
  const preferHintChinese = hintTitleOk
    && /[\u3400-\u9fff]/.test(hintTitle)
    && (!formTitleOk || !/[\u3400-\u9fff]/.test(formTitle));
  const resolvedTitle = (
    preferHintChinese
      ? hintTitle
      : (formTitleOk ? formTitle : (hintTitleOk ? hintTitle : (formTitle || hintTitle)))
  ).trim() || fixedUi('未命名商品');
  form = { ...form, title: resolvedTitle, productName: resolvedTitle };
  let title = resolvedTitle;

  if (!form.categoryId) {
    const earlyFail = failPrepare('validate', '缺少商品类目', { title });
    if (earlyFail) return earlyFail;
  }
  if (!form.productTypeId) {
    const earlyFail = failPrepare('validate', '缺少商品类型，请重新选择末级类目', { title });
    if (earlyFail) return earlyFail;
  }
  if (!collectAllErrors && validationMessages.length) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      messages: validationMessages,
      meta,
    });
  }
  if (!form.categoryId || !form.productTypeId) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      messages: validationMessages,
      meta,
    });
  }

  let attrTpl;
  try {
    attrTpl = await resolveAttrTemplate(
      shopId,
      form.productTypeId,
      attrTplCache,
      form.categoryId || form.productTypeId,
    );
  } catch (ex) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      message: ex?.message || '类目属性加载失败',
      messages: validationMessages,
      meta,
    });
  }

  form = {
    ...form,
    productAttributes: normalizeBrandAttributeSelections(
      alignAttributesToCategoryTemplate(form.productAttributes, attrTpl),
      attrTpl,
    ),
  };

  const colorAttr = attrTpl?.skcSaleAttrs?.[0] || null;
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0] || null;
  const sizeMeasureAttrs = attrTpl?.sizeAttrs || [];
  const resolvedShopType = form?.shopType || shopType || '';

  // 标题语种：中文栏须中文、英文栏须英文；不符则翻译后再写入（自动上架 / 组包共用）
  try {
    form = await ensureTemuFormTitlesLocalized(form, {
      chineseFallback: hintTitleOk && /[\u3400-\u9fff]/.test(hintTitle) ? hintTitle : '',
    });
    title = String(form.title || title).trim() || title;
  } catch (titleErr) {
    const earlyFail = failPrepare(
      'validate',
      titleErr?.message || '标题语种处理失败',
      { title: titleHint || title, field: 'titleEn', section: 'basic' },
    );
    if (earlyFail) return earlyFail;
  }

  // 发品规范 / 尺码类目 / 说明书：互不依赖，并行 + 批量缓存
  const [preflight, sizeClassRes, needGuideFileFlag] = await Promise.all([
    runTemuPublishPreflight({
      shopId,
      form,
      isEdit: false,
      attrTpl,
      shopType: resolvedShopType,
      siteList,
      standardCache: caches.fillInStandard,
    }),
    resolveSizeChartClassCached(
      shopId,
      form.categoryId,
      resolvedShopType,
      caches.sizeChartClass,
    ),
    resolveNeedGuideFileFlag(shopId, form, resolvedShopType, caches.needGuideFile),
  ]);

  if (!preflight.ok) {
    const earlyFail = failPrepare(
      'preflight',
      preflight.message || '发布前校验未通过',
      { title, field: preflight.field, section: preflight.section },
    );
    if (earlyFail) return earlyFail;
  }

  const skuPkgCaps = resolveSkuPackageCapabilities(preflight.standardRaw);
  const resolvedSiteList = preflight.siteList || siteList;
  const resolved = resolveShopPublishCurrencies({
    shopType: resolvedShopType,
    siteList: resolvedSiteList,
    fillInStandard: preflight.standardRaw,
    preferred: form.currency,
    selectedSites: form.selectedSites,
    // 半托/全托：强制店铺默认 USD，避免粘住模板里的 CNY
    forceShopDefault: /半托|全托/i.test(String(resolvedShopType || ''))
      || preflight.standardRaw?.semiManaged === true
      || ['semi', 'full'].includes(String(preflight.standardRaw?.shopTrack || '').toLowerCase()),
  });
  const alignedCurrency = resolved.code;
  if (!alignedCurrency) {
    const currencyMessage = resolved.emptyWarning
      || (resolved.source === 'site-list'
        ? '缺少店铺站点币种，请到店铺同步站点后再试'
        : '缺少商品发布规范币种，请到店铺同步后再试');
    const earlyFail = failPrepare('preflight', currencyMessage, { title });
    if (earlyFail) return earlyFail;
  } else {
    form = { ...form, currency: alignedCurrency, siteList: resolvedSiteList };
  }

  const warehouseOptions = normalizeTemuWarehouseList(warehouses);
  form = withDefaultTemuWarehouse(form, warehouseOptions);

  const sizeChartRequiredFlag = Boolean(sizeClassRes?.ok && sizeClassRes?.required);

  const local = validateTemuPublishForm(form, {
    isEdit: false,
    shopId,
    shopType: resolvedShopType,
    attrTpl,
    colorAttr,
    sizeAttr,
    sizeMeasureAttrs,
    warehouseOptions,
    skuPkgCaps,
    modelRequired: isTemuModelRequiredForCategory(form.categoryId),
    netWeightRequired: isTemuNetWeightRequiredForCategory(form.categoryId),
    semiSkuNetContentRequired: isTemuSemiSkuNetContentRequired({
      categoryId: form.categoryId,
      shopType: resolvedShopType,
    }),
    sizeChartRequired: sizeChartRequiredFlag,
    needGuideFile: needGuideFileFlag,
  });
  if (!local.ok) {
    const earlyFail = failPrepare(
      'validate',
      local.message || '表单校验未通过',
      { title, field: local.field, section: local.section },
    );
    if (earlyFail) return earlyFail;
  }

  // 官方尺码表必填：与手动发品一致；同模板批量只 create 一次并复用
  if (sizeChartRequiredFlag) {
    const draft = form.officialSizeChart && typeof form.officialSizeChart === 'object'
      ? form.officialSizeChart
      : null;
    const existingIds = (Array.isArray(form.temuSizeTemplateIds) ? form.temuSizeTemplateIds : [])
      .map((id) => Number(id))
      .filter((id) => id > 0);
    const sizeCacheKey = sizeTemplateCacheKey(shopId, form);
    try {
      if (caches.sizeTemplateIds.has(sizeCacheKey)) {
        const cachedIds = await caches.sizeTemplateIds.get(sizeCacheKey);
        form = {
          ...form,
          temuSizeTemplateIds: (Array.isArray(cachedIds) ? cachedIds : [])
            .map(String)
            .filter(Boolean),
        };
      } else if (officialSizeChartDraftHasContent(draft, draft?.groupList)) {
        const sizeClass = sizeClassRes?.sizeClass || draft?.sizeClass || null;
        const selectedBusinessIds = Array.isArray(draft.selectedBusinessIds)
          ? draft.selectedBusinessIds
          : [];
        const mode = String(draft.mode || '').trim()
          || (selectedBusinessIds.some((id) => Number(id) > 0) ? 'template' : 'create');
        const pending = resolveOfficialSizeTemplateIds({
          shopId,
          catId: form.categoryId,
          required: true,
          mode,
          sizeClass,
          selectedBusinessIds,
          chartName: '发品尺码表',
          records: Array.isArray(draft.records) ? draft.records : [],
          groupList: Array.isArray(draft.groupList) ? draft.groupList : [],
          elementList: Array.isArray(draft.elementList) ? draft.elementList : [],
          isDoubleSize: Boolean(draft.isDoubleSize) || isTemuShoeSizeClass(sizeClass?.classId),
          api: temuPlatformAPI,
        });
        caches.sizeTemplateIds.set(sizeCacheKey, pending);
        pending.catch(() => { caches.sizeTemplateIds.delete(sizeCacheKey); });
        const resolvedIds = await pending;
        form = {
          ...form,
          temuSizeTemplateIds: (Array.isArray(resolvedIds) ? resolvedIds : [])
            .map(String)
            .filter(Boolean),
        };
      } else if (!existingIds.length) {
        throw new Error('当前类目要求官方尺码表，请先在产品模板中填写尺码表后再自动上款');
      }
    } catch (sizeErr) {
      const earlyFail = failPrepare(
        'validate',
        sizeErr?.message || '官方尺码表处理失败',
        { title, field: 'sizeChart', section: 'spec' },
      );
      if (earlyFail) return earlyFail;
    }
  }

  if (collectAllErrors && validationMessages.length) {
    return buildPrepareFailure({
      draftId,
      title,
      stage: 'validate',
      messages: validationMessages,
      meta,
    });
  }

  // 自定义颜色：与发品页一致，createPublishSpec 拿平台 specId
  if (colorAttr) {
    try {
      const nextSkc = await ensureTemuCustomColorSpecs({
        shopId,
        skcList: form.skcList,
        colorAttr,
        attrTpl,
        api: temuPlatformAPI,
      });
      form = { ...form, skcList: nextSkc };
    } catch (specErr) {
      const earlyFail = failPrepare(
        'validate',
        specErr?.message || '自定义颜色规格创建失败',
        { title, field: 'skcList', section: 'spec' },
      );
      if (earlyFail) return earlyFail;
    }
  }

  const submitFormBase = buildSubmitForm(form, { colorAttr, sizeAttr, alignedCurrency });
  const submitForm = {
    ...submitFormBase,
    sizeChart: pruneSizeChartForSubmit(submitFormBase, sizeMeasureAttrs),
  };

  let payload;
  try {
    payload = buildTemuPublishPayload(submitForm, {
      isEdit: false,
      sizeAttrs: sizeMeasureAttrs,
      skuPkgCaps,
      shopType: form?.shopType || shopType || '',
      siteList: resolvedSiteList,
      attrTpl,
    });
  } catch (buildErr) {
    return {
      ok: false,
      draftId,
      title,
      stage: 'build',
      message: buildErr?.message || '无法组装发布参数',
      ...meta,
    };
  }

  const sampleCurrency = samplePublishPayloadCurrency(payload);
  if (!sampleCurrency) {
    return {
      ok: false,
      draftId,
      title,
      stage: 'build',
      message: resolved.source === 'site-list'
        ? '发品参数缺少售价币种（须来自店铺可售站点）'
        : '发品参数缺少供货价币种（须来自商品发布规范）',
      ...meta,
    };
  }

  return {
    ok: true,
    draftId,
    title,
    submitForm,
    payload,
    ...meta,
  };
}

/**
 * 单条草稿：拉详情后走与发品页相同的准备链路。
 */
export async function prepareDraftPublish({
  shopId,
  draftId,
  draftMeta = {},
  warehouses = [],
  attrTplCache,
  shopType = '',
  siteList = null,
  prepareCaches = null,
}) {
  const titleHint = draftMeta.title || draftMeta.supplierCode || `草稿#${draftId}`;
  const detail = await temuPlatformAPI.getPublishDraft(shopId, draftId);
  return prepareFormPublish({
    shopId,
    form: detail?.form || {},
    titleHint,
    draftId,
    warehouses,
    attrTplCache,
    shopType,
    siteList,
    prepareCaches,
  });
}

function draftFormPayload(submitForm) {
  return {
    form: {
      ...submitForm,
      selectedSizeIds: (submitForm.selectedSizeIds?.length
        ? submitForm.selectedSizeIds
        : deriveSelectedSizeIds(submitForm.skcList)
      ).map((id) => String(id)),
    },
  };
}

/**
 * 保存草稿并调用 publish-or-edit（立即返回 job，不等待平台终态；与发品页入队一致）。
 */
export async function enqueuePreparedPublish({
  shopId,
  prepared,
  onHint,
}) {
  const { title, submitForm, payload } = prepared;
  let draftId = prepared.draftId;

  try {
    onHint?.(`正在保存草稿「${title}」…`);
    if (draftId != null && draftId !== '') {
      await temuPlatformAPI.updatePublishDraft(shopId, draftId, draftFormPayload(submitForm));
    } else {
      const draftRes = await temuPlatformAPI.createPublishDraft(shopId, draftFormPayload(submitForm));
      if (draftRes?.id != null) draftId = String(draftRes.id);
    }
  } catch {
    // 与发品页一致：保存草稿失败不阻断提交
  }

  onHint?.(`正在提交「${title}」…`);
  const params = draftId != null && draftId !== ''
    ? { draftId: Number(draftId) || draftId }
    : {};
  const job = await temuPlatformAPI.publishOrEditProduct(shopId, payload, params);
  return {
    ...prepared,
    draftId,
    job,
    jobId: job?.jobId || null,
  };
}

function finalizePublishJobResult(prepared, job) {
  const { draftId, title } = prepared;
  const jobId = job?.jobId || prepared.jobId || null;
  let platformRes = job?.response;
  if (!platformRes && job && (job.code != null || job.info != null)) {
    platformRes = job;
  }
  if (!platformRes && job?.status === 'failed') {
    return {
      ok: false,
      draftId,
      title,
      stage: 'publish',
      message: job?.error || job?.message || '提交失败',
      jobId,
      listingItemId: prepared.listingItemId,
    };
  }

  const res = platformRes || job?.response || job;
  const ok = isTemuPublishSuccess(res)
    || job?.ok === true
    || String(job?.status || '').toLowerCase() === 'success';
  if (!ok) {
    return {
      ok: false,
      draftId,
      title,
      stage: 'publish',
      message: extractTemuPublishFailMessage(res) || job?.error || job?.message || '发布未成功',
      jobId,
      listingItemId: prepared.listingItemId,
    };
  }

  return {
    ok: true,
    draftId,
    title,
    stage: 'publish',
    jobId,
    publishedSpu: job?.publishedSpu
      || extractTemuPublishedSpu(res, prepared?.payload || {}, prepared?.submitForm?.spuName)
      || null,
    platformRes: res,
    form: prepared?.submitForm || null,
    message: '已提交发布审核',
    listingItemId: prepared.listingItemId,
  };
}

/**
 * 提交并轮询至任务结束（草稿箱批量仍用此路径，同店串行）。
 */
export async function submitPreparedDraftPublish({
  shopId,
  prepared,
  onHint,
}) {
  let enqueued;
  try {
    enqueued = await enqueuePreparedPublish({ shopId, prepared, onHint });
  } catch (ex) {
    return {
      ok: false,
      draftId: prepared.draftId,
      title: prepared.title,
      stage: 'publish',
      message: ex?.message || '提交失败',
      listingItemId: prepared.listingItemId,
    };
  }

  let job = enqueued.job;
  const jobId = job?.jobId;
  if (jobId && !job?.done) {
    job = await waitGoodsSubmitJob(shopId, jobId, onHint);
  }
  return finalizePublishJobResult(enqueued, job);
}

/**
 * 仅轮询已入队任务，得到与 submitPreparedDraftPublish 相同结构的结果。
 */
export async function pollEnqueuedPublishResult({
  shopId,
  enqueued,
  onHint,
}) {
  let job = enqueued?.job;
  const jobId = job?.jobId || enqueued?.jobId;
  if (jobId && (!job || !job.done)) {
    job = await waitGoodsSubmitJob(shopId, jobId, onHint);
  }
  return finalizePublishJobResult(enqueued, job || enqueued?.job);
}

/**
 * @param {object} opts
 * @param {string|number} opts.shopId
 * @param {Array<{ id: string|number, title?: string, supplierCode?: string }>} opts.drafts
 * @param {number} [opts.concurrency=40]
 * @param {(progress: { phase: string, processed: number, total: number, message: string, last?: object }) => void} [opts.onProgress]
 * @returns {Promise<{ success: object[], failed: object[], total: number }>}
 */
export async function batchPublishDrafts({
  shopId,
  drafts,
  shopType = '',
  concurrency = TEMU_BATCH_PUBLISH_CONCURRENCY,
  onProgress,
}) {
  const list = (Array.isArray(drafts) ? drafts : []).filter((d) => d?.id != null);
  const total = list.length;
  if (!shopId || !total) {
    return { success: [], failed: [], total: 0 };
  }

  let warehouses = [];
  try {
    const wh = await temuPlatformAPI.getShopWarehouses(shopId);
    warehouses = normalizeTemuWarehouseList(wh);
  } catch {
    warehouses = [];
  }

  let siteList = null;
  try {
    const siteRes = await temuPlatformAPI.getShopSiteList(shopId);
    siteList = siteRes?.siteList ?? null;
  } catch {
    siteList = null;
  }

  const attrTplCache = new Map();
  const prepareCaches = emptyPrepareCaches();
  const prepConcurrency = Math.max(1, Math.min(Number(concurrency) || TEMU_BATCH_PUBLISH_CONCURRENCY, TEMU_BATCH_PUBLISH_CONCURRENCY));

  onProgress?.({
    phase: 'preflight',
    processed: 0,
    total,
    message: `正在校验 0/${total}…（规则 / 属性 / SKU）`,
  });

  const prepared = await mapWithConcurrency(
    list,
    prepConcurrency,
    async (draft) => {
      try {
        return await prepareDraftPublish({
          shopId,
          draftId: draft.id,
          draftMeta: draft,
          warehouses,
          attrTplCache,
          shopType,
          siteList,
          prepareCaches,
        });
      } catch (ex) {
        return {
          ok: false,
          draftId: draft.id,
          title: draft.title || draft.supplierCode || `草稿#${draft.id}`,
          stage: 'prepare',
          message: ex?.message || '准备发布失败',
        };
      }
    },
    (done, all, result) => {
      onProgress?.({
        phase: 'preflight',
        processed: done,
        total: all,
        message: result?.ok
          ? `校验通过 ${done}/${all}：${result.title || ''}`
          : `校验失败 ${done}/${all}：${result?.message || ''}`,
        last: result,
      });
    },
  );

  const ready = prepared.filter((row) => row?.ok);
  const failed = prepared.filter((row) => row && !row.ok);
  const success = [];

  onProgress?.({
    phase: 'publish',
    processed: failed.length,
    total,
    message: ready.length
      ? `校验完成，开始发布 0/${ready.length}…`
      : '全部未通过校验',
  });

  // 同店发品任务 FIFO：提交串行，避免无 MQ 时多请求互锁超时
  for (let i = 0; i < ready.length; i += 1) {
    const item = ready[i];
    onProgress?.({
      phase: 'publish',
      processed: failed.length + success.length,
      total,
      message: `正在发布 ${i + 1}/${ready.length}：${item.title || item.draftId}`,
    });
    // eslint-disable-next-line no-await-in-loop
    const submitted = await submitPreparedDraftPublish({
      shopId,
      prepared: item,
      onHint: (hint) => {
        onProgress?.({
          phase: 'publish',
          processed: failed.length + success.length,
          total,
          message: hint || `正在发布 ${i + 1}/${ready.length}`,
        });
      },
    });
    if (submitted.ok) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await temuPlatformAPI.createPublishRecord(shopId, {
          form: submitted.form || item.submitForm || {},
          platformRes: submitted.platformRes || null,
          publishedSpu: submitted.publishedSpu || '',
          draftId: submitted.draftId || item.draftId,
          action: 'publish',
        });
      } catch {
        /* ignore */
      }
      const cleanupId = submitted.draftId || item.draftId;
      if (cleanupId != null && cleanupId !== '') {
        try {
          // eslint-disable-next-line no-await-in-loop
          await temuPlatformAPI.deletePublishDraft(shopId, cleanupId);
        } catch {
          /* 清理失败不阻断批量 */
        }
      }
      success.push(submitted);
    } else failed.push(submitted);

    onProgress?.({
      phase: 'publish',
      processed: failed.length + success.length,
      total,
      message: submitted.ok
        ? `已发布 ${success.length}/${ready.length}：${submitted.title || ''}`
        : `发布失败：${submitted.message || ''}`,
      last: submitted,
    });
  }

  return { success, failed, total };
}
